import { styled } from '@linaria/react';
import { Group, Modal, Stack, Tooltip } from '@mantine/core';
import { useClipboard, useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import {
  useMcpTokenStatus,
  useRegenerateMcpToken,
  useRevokeMcpToken,
} from '../../../api/mcp-tokens-client/mcp-tokens-client.hooks';
import { baseWhite, inkBase, inkLight, redBase, secondaryBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';

/**
 * Settings tab that lets the authenticated user view, regenerate, and revoke
 * their personal API token (used by the MCP server to authenticate as them).
 *
 * Responsibilities:
 * 1. Display whether the user currently has an active token
 * 2. Let the user generate or regenerate a token, surfacing the raw value
 *    exactly once in a non-dismissable modal that warns it cannot be shown again
 * 3. Let the user revoke an existing token after explicit confirmation
 *
 * @returns {JSX.Element} The API Keys tab content.
 */
export function ApiKeysTab() {
  const statusQuery = useMcpTokenStatus();
  const regenerateMutation = useRegenerateMcpToken();
  const revokeMutation = useRevokeMcpToken();

  const [tokenModalOpened, tokenModal] = useDisclosure(false);
  const [regenerateConfirmOpened, regenerateConfirm] = useDisclosure(false);
  const [revokeConfirmOpened, revokeConfirm] = useDisclosure(false);
  const [rawToken, setRawToken] = useState<string | null>(null);

  const clipboard = useClipboard({ timeout: 2000 });
  const hasToken = !!statusQuery.data?.has_token;

  const runRegenerate = useCallback(async () => {
    regenerateConfirm.close();
    try {
      const result = await regenerateMutation.mutateAsync();
      setRawToken(result.token);
      tokenModal.open();
    } catch {
      // Errors surfaced inline via mutation state below
    }
  }, [regenerateConfirm, regenerateMutation, tokenModal]);

  const handleGenerateClick = useCallback(() => {
    if (hasToken) {
      regenerateConfirm.open();
    } else {
      void runRegenerate();
    }
  }, [hasToken, regenerateConfirm, runRegenerate]);

  const handleRevoke = useCallback(async () => {
    revokeConfirm.close();
    try {
      await revokeMutation.mutateAsync();
    } catch {
      // Errors surfaced inline via mutation state below
    }
  }, [revokeConfirm, revokeMutation]);

  const closeTokenModal = useCallback(() => {
    tokenModal.close();
    setRawToken(null);
  }, [tokenModal]);

  return (
    <Wrapper>
      <Stack gap={8} style={{ maxWidth: 720, width: '100%' }}>
        <H3>API Token</H3>
        <Text color={inkLight}>
          Generate a personal API token to authenticate the MCP server (and other Bloomfilter integrations) as your
          account. The token grants the same access as your normal login, so treat it like a password.
        </Text>

        {statusQuery.isLoading ? (
          <Text color={inkLight}>Loading token status&hellip;</Text>
        ) : statusQuery.isError ? (
          <Text color={redBase}>Failed to load token status. Please refresh and try again.</Text>
        ) : hasToken ? (
          <ActiveCard>
            <Group justify="space-between" align="center">
              <Stack gap={2}>
                <Text weight="bold" color={inkBase}>
                  Token Active
                </Text>
                <Text color={inkLight} size="small">
                  Expires:{' '}
                  {statusQuery.data?.expires_at ? dayjs(statusQuery.data.expires_at).format('MMM D, YYYY') : 'Never'}
                </Text>
                <Text color={inkLight} size="small">
                  Last used:{' '}
                  {statusQuery.data?.last_used_at ? dayjs(statusQuery.data.last_used_at).format('MMM D, YYYY') : '—'}
                </Text>
              </Stack>
              <Group gap={8}>
                <Button
                  variant="outline"
                  radius="xl"
                  size="sm"
                  onClick={handleGenerateClick}
                  loading={regenerateMutation.isPending}
                  data-bf="api-keys-regenerate"
                  leftSection={<Icon name="autorenew" size={16} color={inkBase} />}
                >
                  <Text weight="medium">Regenerate</Text>
                </Button>
                <Button
                  variant="outline"
                  radius="xl"
                  size="sm"
                  onClick={revokeConfirm.open}
                  loading={revokeMutation.isPending}
                  data-bf="api-keys-revoke"
                  leftSection={<Icon name="delete" size={16} color={redBase} />}
                >
                  <Text weight="medium" color={redBase}>
                    Revoke
                  </Text>
                </Button>
              </Group>
            </Group>
          </ActiveCard>
        ) : (
          <NoTokenCard>
            <Text color={inkLight}>
              You don't have an API token yet. Generate one to start using the MCP server with your Bloomfilter account.
            </Text>
            <Button
              variant="primary"
              radius="xl"
              size="sm"
              onClick={handleGenerateClick}
              loading={regenerateMutation.isPending}
              data-bf="api-keys-generate"
              leftSection={<Icon name="key" size={16} color={baseWhite} />}
            >
              <Text weight="medium" color={baseWhite}>
                Generate API Token
              </Text>
            </Button>
          </NoTokenCard>
        )}

        {regenerateMutation.isError && <Text color={redBase}>Failed to regenerate token. Please try again.</Text>}
        {revokeMutation.isError && <Text color={redBase}>Failed to revoke token. Please try again.</Text>}
      </Stack>

      <Modal
        opened={regenerateConfirmOpened}
        onClose={regenerateConfirm.close}
        centered
        radius="lg"
        size="md"
        padding="32px"
        withCloseButton={false}
        closeOnClickOutside
      >
        <Stack gap={16}>
          <H3>Regenerate API token?</H3>
          <Text color={inkLight}>
            Regenerating will immediately invalidate your existing token. Any tool currently using it will stop working
            until you update it with the new value.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" radius="xl" onClick={regenerateConfirm.close}>
              <Text weight="medium" color={inkLight}>
                Cancel
              </Text>
            </Button>
            <Button
              variant="primary"
              radius="xl"
              onClick={runRegenerate}
              loading={regenerateMutation.isPending}
              data-bf="api-keys-regenerate-confirm"
            >
              <Text weight="medium" color={baseWhite}>
                Regenerate
              </Text>
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={revokeConfirmOpened}
        onClose={revokeConfirm.close}
        centered
        radius="lg"
        size="md"
        padding="32px"
        withCloseButton={false}
        closeOnClickOutside
      >
        <Stack gap={16}>
          <H3>Revoke API token?</H3>
          <Text color={inkLight}>
            Revoking will immediately invalidate your API token. Any tool currently using it will stop working. You can
            generate a new one later.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" radius="xl" onClick={revokeConfirm.close}>
              <Text weight="medium" color={inkLight}>
                Cancel
              </Text>
            </Button>
            <Button
              variant="primary"
              radius="xl"
              onClick={handleRevoke}
              loading={revokeMutation.isPending}
              style={{ backgroundColor: redBase }}
              data-bf="api-keys-revoke-confirm"
            >
              <Text weight="medium" color={baseWhite}>
                Revoke
              </Text>
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={tokenModalOpened}
        onClose={closeTokenModal}
        centered
        radius="lg"
        size="lg"
        padding="32px"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack gap={16}>
          <H3>Save your API token</H3>
          <WarningBanner>
            <Icon name="warning" size={20} color={redBase} style={{ marginTop: 2, flexShrink: 0 }} />
            <Text weight="medium" color={redBase} size="small">
              This token is shown only once. Copy it now — you won't be able to retrieve it later. If you lose it, you
              can regenerate a new one.
            </Text>
          </WarningBanner>
          <TokenBlock>
            <code>{rawToken}</code>
            <Tooltip label={clipboard.copied ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                variant="subtle"
                radius="md"
                size="xs"
                onClick={() => rawToken && clipboard.copy(rawToken)}
                data-bf="api-keys-copy-token"
                aria-label="Copy token to clipboard"
              >
                <Icon
                  name={clipboard.copied ? 'check' : 'content_copy'}
                  size={18}
                  color={clipboard.copied ? secondaryBase : skyDark}
                />
              </Button>
            </Tooltip>
          </TokenBlock>
          <Group justify="flex-end">
            <Button variant="primary" radius="xl" onClick={closeTokenModal} data-bf="api-keys-token-modal-close">
              <Text weight="medium" color={baseWhite}>
                I've saved it
              </Text>
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 16px;
  width: 100%;
`;

const ActiveCard = styled.div`
  padding: 20px;
  border-radius: 8px;
  background-color: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  margin-top: 8px;
`;

const NoTokenCard = styled.div`
  padding: 20px;
  border-radius: 8px;
  background-color: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const WarningBanner = styled.div`
  background-color: rgba(255, 82, 71, 0.1);
  border: 1px solid ${redBase};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const TokenBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background-color: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  border-radius: 8px;
  padding: 12px 16px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  word-break: break-all;

  code {
    flex: 1;
    color: ${inkBase};
  }
`;
